import {
  IconFeatureCard,
  ImageCardSmall,
  TwoColumnImageCard,
} from "@repo/ui/components/common/cms";
import type {
  IconFeatureCard as IconFeatureCardType,
  ImageCard,
  LinkedItem,
} from "@repo/ui/types/cms";

import HeaderLayout from "../HeaderLayout";
import MobileBenefits from "./MobileBenefits";

interface BenefitsProps {
  linkedItems: LinkedItem[];
  title?: string;
}

/** Figma order: Reservas → Empresas → Agencias */
const OTROS_BENEFICIOS_ORDER = [
  ["reserva", "grupo", "group"],
  ["empresa", "compan"],
  ["agencia", "agenc"],
];

function getOtrosBeneficiosSortIndex(item: IconFeatureCardType): number {
  const title = item.title?.toLowerCase() ?? "";
  const index = OTROS_BENEFICIOS_ORDER.findIndex((keywords) =>
    keywords.some((keyword) => title.includes(keyword)),
  );
  return index === -1 ? OTROS_BENEFICIOS_ORDER.length : index;
}

function sortOtrosBeneficios(cards: IconFeatureCardType[] = []): IconFeatureCardType[] {
  return [...cards].sort((a, b) => getOtrosBeneficiosSortIndex(a) - getOtrosBeneficiosSortIndex(b));
}

export default function Benefits({ linkedItems, title }: Readonly<BenefitsProps>) {
  const [benefits, features, programs] = linkedItems;
  const otrosBeneficios = sortOtrosBeneficios(features?.icon_feature_cards);

  return (
    <HeaderLayout>
      <div className="md:hidden" data-testid="benefits-mobile-section">
        <MobileBenefits />
      </div>
      <div
        className="text-brand-blue container mx-auto hidden md:block"
        data-testid="benefits-desktop-section"
      >
        <h2 className="mb-4 text-2xl font-bold" data-testid="benefits-title-lbl">
          {title ?? "No te pierdas estos beneficios"}
        </h2>

        <div className="flex gap-6 pb-2" data-testid="benefits-content">
          <div className="w-7/9" data-testid="benefits-programs-section">
            {programs?.heading && (
              <h3
                className="text-brand-blue mb-4 text-base font-semibold"
                data-testid="benefits-programs-heading-lbl"
              >
                {programs.heading || "Programas SMART"}
              </h3>
            )}

            <div
              className="grid grid-cols-7 items-stretch gap-4"
              data-testid="benefits-programs-grid"
            >
              <div
                className="col-span-3 overflow-hidden rounded-lg"
                data-testid="benefits-programs-main"
              >
                {programs?.title && <TwoColumnImageCard item={programs} />}
              </div>

              <div
                className="col-span-4 grid grid-cols-2 gap-4"
                data-testid="benefits-programs-cards"
              >
                {benefits?.image_cards?.map((item: ImageCard) => (
                  <ImageCardSmall key={item.heading} item={item} />
                ))}
              </div>
            </div>
          </div>

          <div className="w-2/9" data-testid="benefits-features-section">
            {features?.title && (
              <h3
                className="text-brand-blue mb-4 text-base font-semibold"
                data-testid="benefits-features-heading-lbl"
              >
                {features.title || "Otros beneficios"}
              </h3>
            )}
            <div className="space-y-3" data-testid="benefits-features-list">
              {otrosBeneficios.map((item: IconFeatureCardType) => (
                <IconFeatureCard
                  key={item.title}
                  item={item}
                  testId={`benefits-feature-card-${item.title.replace(/\s+/g, "-").toLowerCase()}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </HeaderLayout>
  );
}
